// Scheduler Main content component
import { useRef } from 'react';

import clsx from 'clsx';

import { useShouldScroll } from '@isd/lib/helpers/hooks';

import { useEditSchedulerContext } from '@/context/EditSchedulerContext';
import { useSchedulerContext } from '@/context/SchedulerContext';

import JobsList from '@/components/Job/JobsList';
import LiveMapView from '@/components/LiveMap/LiveMapView';
import OperatorSearchWidget from '@/components/OperatorSearch/OperatorSearchWidget';
import ActiveFiltersBar from '@/components/Scheduler/SchedulerMain/ActiveFiltersBar';

export default function SchedulerMain() {
  const { schedulerView } = useSchedulerContext();
  return (
    <div className="flex h-full flex-col">
      <ActiveFiltersBar />
      <div className="min-h-0 flex-1">
        {schedulerView === 'map' ? <SchedulerMapView /> : <SchedulerListView />}
      </div>
    </div>
  );
}

/**
 * The map of the day's jobs, trucks and sites.
 * @returns {React.JSX.Element}
 */
function SchedulerMapView() {
  return (
    <div className="scheduler-main h-full px-2 pt-2 pb-3 md:px-5">
      <LiveMapView />
    </div>
  );
}

/**
 * The list of job cards, with the operator search beside it while assigning.
 * @returns {React.JSX.Element}
 */
function SchedulerListView() {
  const { selectedGroup, selectedJob } = useEditSchedulerContext();
  const jobsListContainer = useRef<HTMLDivElement | null>(null);
  const shouldScrollJobsList = useShouldScroll(jobsListContainer);

  return (
    <div className="scheduler-main relative grid h-full grid-cols-4 justify-center">
      {!!selectedJob && selectedGroup !== null && (
        <div className="col-span-1 hidden pt-2 pl-1 md:block lg:pl-5">
          <OperatorSearchWidget />
        </div>
      )}
      <div
        ref={jobsListContainer}
        className={clsx(
          'jobs-list-container col-span-4 overflow-x-hidden px-2 md:py-6',
          'transition-all', // Animate expansion and contraction of Jobs List
          shouldScrollJobsList && 'overflow-y-scroll',
          selectedGroup === null
            ? 'xl:px-10' // Search widget closed
            : 'md:col-span-3' // Search widget open
        )}
      >
        <JobsList />
      </div>
    </div>
  );
}
